/**
 * Typed, content-free summary of an editor apply/selection failure for
 * application logs (OpenSpec `monorepo-kit-editor` task 4.10).
 *
 * TipTap/ProseMirror errors can echo document content in their message —
 * `Invalid content for node doc: {…note json…}` — so a raw error in a log
 * payload ships note content to the log sink. Only the constructor name and a
 * classification matched against known content-free shapes may cross into
 * logging; never the message, stack, cause, or enumerable properties.
 */
export interface SafeEditorErrorSummary {
  /** Error constructor name (`Error`, `RangeError`, …) or the thrown value's typeof. */
  name: string;
  /** Stable, content-free failure class for log queries and dashboards. */
  classification: 'invalid-content' | 'selection-range' | 'unknown';
}

/**
 * Content-free prefixes of the known TipTap/ProseMirror failure shapes. Only
 * the prefix is consulted — the remainder of the message (which may embed
 * document content) never influences or reaches the summary.
 */
const CLASSIFICATION_PATTERNS: ReadonlyArray<{
  classification: SafeEditorErrorSummary['classification'];
  pattern: RegExp;
}> = [
  // @tiptap/markdown + ProseMirror content rejection on hydrate/serialize.
  {
    classification: 'invalid-content',
    pattern: /^(\[tiptap error]:\s*)?Invalid content for node/i,
  },
  // ProseMirror position/selection failures on restore after an apply.
  {
    classification: 'selection-range',
    pattern: /^(Position \d+ out of range|There is no position (before|after))/,
  },
];

export const toSafeEditorErrorSummary = (error: unknown): SafeEditorErrorSummary => {
  const name = error instanceof Error ? error.name : typeof error;
  const message = error instanceof Error ? error.message : '';
  const matched = CLASSIFICATION_PATTERNS.find(({ pattern }) => pattern.test(message));
  return { name, classification: matched?.classification ?? 'unknown' };
};
